import styled, { css, keyframes } from "styled-components";

// Animations
const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

// Layout Components
export const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 25px;
  flex-wrap: wrap;
  padding-bottom: 15px;
  border-bottom: 2px solid var(--accent-light);
`;

export const SectionTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: var(--text-primary);
  text-transform: uppercase;
  letter-spacing: 1px;
  margin: 0;
`;

export const SectionActions = styled.div`
  display: flex;
  gap: 15px;
`;

export const Button = styled.button`
  padding: 12px 25px;
  border: none;
  border-radius: 20px;
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  cursor: pointer;
  transition: transform 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  text-decoration: none;

  ${(props) =>
    props.$small &&
    `
    padding: 8px 16px;
    font-size: 12px;
  `}

  ${(props) =>
    props.$primary &&
    `
    background: var(--gradient-primary);
    color: var(--surface);
    
    &:hover:not(:disabled) {
      background: var(--accent-dark);
      color: var(--surface);
      transform: translateY(-2px);
    }
  `}

  ${(props) =>
    props.$secondary &&
    `
    background: transparent;
    color: var(--text-secondary);
    border: 2px solid var(--accent-light);
    
    &:hover:not(:disabled) {
      background: var(--accent-light);
      border-color: var(--accent);
      color: var(--accent);
      transform: translateY(-2px);
    }
  `}

   svg {
    animation: ${(props) =>
      props.$loading
        ? css`
            ${spin} 1s ease-in-out infinite
          `
        : "none"};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

// Filter Components
export const Filters = styled.div`
  display: flex;
  gap: 15px;
  margin-bottom: 25px;
  flex-wrap: wrap;
`;

export const FilterInput = styled.input`
  padding: 12px 20px;
  border: 2px solid var(--accent-light);
  border-radius: 25px;
  font-family: inherit;
  max-height: 38px;
  font-size: 14px;
  background: var(--surface);
  color: var(--text-primary);
  transition: all 0.3s ease;
  outline: none;
  width: 100%;
  max-width: 300px;

  &:focus {
    border-color: var(--accent);
    background: var(--surface);
    box-shadow: 0 0 0 3px rgba(124, 14, 51, 0.1);
  }

  &::placeholder {
    color: var(--text-muted);
  }
`;

// Table Components
export const DataTableScroll = styled.div`
  width: 100% !important;
  overflow-x: auto !important;
  overflow-y: visible !important;
  border-radius: 15px;
  box-shadow: var(--shadow-soft);
  max-width: none !important;
  min-width: 0 !important;
  flex-shrink: 0 !important;
`;

export const DataTable = styled.div`
  display: grid;
  grid-template-columns: 250px 300px 180px 100px 1fr;
  min-width: 986px;

  background: var(--surface);
  border-radius: 15px;
  border: 2px solid var(--accent-light);
  overflow: hidden;
  animation: ${slideIn} 0.6s ease-out;
`;

export const TableHeader = styled.div`
  padding: 15px 12px;
  border-bottom: 2px solid var(--accent-light);
  background-color: var(--accent);
  font-weight: 600;
  font-size: 12px;
  text-transform: uppercase;
  white-space: nowrap;
  letter-spacing: 1px;
  color: white;
  text-align: ${(props) => props.$align || "left"};
  display: flex;
  align-items: center;
  justify-content: ${(props) => props.$align || "left"};
`;

export const TableRow = styled.div`
  display: contents;
  cursor: pointer;
  &:hover > div {
    background: rgba(124, 14, 51, 0.05);
  }
`;

export const TableCell = styled.div`
  padding: 15px 20px;
  text-align: left;
  border-bottom: 1px solid var(--accent-light);
  white-space: nowrap;
  font-size: 14px;
  color: var(--text-primary);
  display: flex;
  align-items: center;
`;

export const LoadingCell = styled.div`
  grid-column: 1 / -1;
  text-align: center;
  padding: 20px;
`;

export const EmptyCell = styled.div`
  grid-column: 1 / -1;
  text-align: center;
  padding: 20px;
  color: var(--text-muted);
`;

// User Components
export const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const UserAvatar = styled.div`
  width: 35px;
  height: 35px;
  border-radius: 50%;
  background: var(--gradient-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 12px;
  color: white;
  text-transform: uppercase;
`;

export const UserAvatarStyled = styled(UserAvatar)`
  background: ${(props) =>
    props.$active ? "var(--gradient-primary)" : "var(--text-muted)"};
  border: 2px solid
    ${(props) => (props.$active ? "var(--accent)" : "var(--accent-light)")};
  transition: all 0.3s ease;
`;

export const UserName = styled.div`
  font-weight: 600;
  color: var(--text-primary);
  font-size: 14px;
`;

export const UserId = styled.div`
  font-size: 11px;
  color: var(--text-muted);
  font-family: monospace;
`;

// Email and Date Components
export const EmailCell = styled(TableCell)`
  color: var(--text-secondary);
  font-size: 13px;
`;

export const DateCell = styled(TableCell)`
  color: var(--text-secondary);
  font-size: 13px;
`;

// Course and Spending Components
export const CourseCountCell = styled(TableCell)`
  justify-content: center;
  font-weight: 600;
  font-size: 14px;
  color: ${(props) =>
    props.$hasContent ? "var(--accent)" : "var(--text-muted)"};
`;

export const SpendingCell = styled(TableCell)`
  justify-content: flex-end;
  font-weight: 600;
  font-size: 14px;
  color: ${(props) =>
    props.$hasSpending ? "var(--success)" : "var(--text-muted)"};
`;

// Action Components
export const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
  justify-content: center;
`;

export const ActionBtn = styled.button`
  padding: 8px;
  border: 1px solid;
  border-radius: 6px;
  background: transparent;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  ${(props) => {
    switch (props.$type) {
      case "view":
        return `
          background: var(--info);
          border-color: var(--info);
          color: white;
          
          &:hover {
            background: var(--info-dark);
            border-color: var(--info-dark);
          }
        `;
      case "edit":
        return `
          background: var(--warning);
          border-color: var(--warning);
          color: white;
          
          &:hover {
            background: var(--warning-dark);
            border-color: var(--warning-dark);
          }
        `;
      case "delete":
        return `
          background: var(--error);
          border-color: var(--error);
          color: white;
          
          &:hover {
            background: var(--error-dark);
            border-color: var(--error-dark);
          }
        `;
      default:
        return `
          border-color: var(--accent-light);
          color: var(--text-secondary);
          
          &:hover {
            background: var(--accent-light);
            border-color: var(--accent);
            color: var(--accent);
          }
        `;
    }
  }}
`;

// Pagination Components
export const PaginationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 25px;
  padding: 20px;
  background-color: var(--surface);
  border-radius: 12px;
  box-shadow: var(--shadow-soft);
  border: 1px solid var(--accent-light);
  gap: 12px;
  @media (max-width: 490px) {
    flex-direction: column;
    justify-content: center;
  }
`;

export const PaginationInfo = styled.div`
  color: var(--text-secondary);
  font-size: 14px;
  font-weight: 500;

  strong {
    color: var(--accent);
  }
`;

export const PaginationControls = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

export const PaginationButton = styled(Button)`
  padding: 8px 16px;
  font-size: 12px;
  opacity: ${(props) => (props.$disabled ? 0.5 : 1)};
  cursor: ${(props) => (props.$disabled ? "not-allowed" : "pointer")};
  @media (max-width: 825px) {
    border-radius: 12px;
    border-width: 1px;
  }
`;

export const PageNumbers = styled.div`
  display: flex;
  gap: 4px;
  margin: 0 10px;
`;

export const PageNumber = styled.button`
  padding: 6px 10px;
  border: ${(props) =>
    props.$active
      ? "2px solid var(--accent)"
      : "1px solid var(--accent-light)"};
  background-color: ${(props) =>
    props.$active ? "var(--accent)" : "var(--surface)"};
  color: ${(props) => (props.$active ? "white" : "var(--text-secondary)")};
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  font-weight: ${(props) => (props.$active ? "600" : "400")};
  transition: all 0.2s ease;
  min-width: 32px;

  &:hover {
    background-color: ${(props) =>
      props.$active ? "var(--accent)" : "var(--accent-light)"};
    border-color: var(--accent);
  }
`;

// Modal Components
export const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

export const ModalContent = styled.div`
  background-color: var(--surface);
  border-radius: 12px;
  padding: 30px;
  max-width: 800px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
`;

export const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 25px;
  border-bottom: 1px solid var(--accent-light);
  padding-bottom: 15px;
`;

export const ModalTitle = styled.h2`
  margin: 0;
  color: var(--text-primary);
  font-size: 24px;
  font-weight: 600;
`;

export const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: var(--text-muted);
  padding: 5px;
`;

export const ModalGrid = styled.div`
  display: grid;
  gap: 25px;
`;

// User Details Components
export const UserDetailsHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 20px;
  background-color: var(--background);
  border-radius: 8px;
`;

export const UserDetailsAvatar = styled(UserAvatar)`
  width: 60px;
  height: 60px;
  font-size: 24px;
  background-color: var(--accent);
`;

export const UserDetailsInfo = styled.div``;

export const UserDetailsName = styled.h3`
  margin: 0 0 5px 0;
  font-size: 20px;
  color: var(--text-primary);
`;

export const UserDetailsId = styled.p`
  margin: 0;
  color: var(--text-muted);
  font-size: 14px;
`;

// Contact Info Grid
export const ContactInfoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
`;

export const InfoField = styled.div``;

export const InfoLabel = styled.label`
  display: block;
  font-size: 12px;
  color: var(--text-muted);
  margin-bottom: 5px;
  text-transform: uppercase;
  font-weight: 600;
`;

export const InfoValue = styled.p`
  margin: 0;
  color: var(--text-primary);
  font-size: 14px;
`;

// Stats Grid
export const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 15px;
  margin-top: 10px;
`;

export const StatCard = styled.div`
  text-align: center;
  padding: 15px;
  background-color: var(--background);
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

export const StatValue = styled.div`
  font-size: 24px;
  font-weight: 600;
  color: var(--accent);
`;

export const StatLabel = styled.div`
  font-size: 12px;
  color: var(--text-muted);
  margin-top: 5px;
`;

// Registration Date Section
export const RegistrationSection = styled.div`
  padding: 20px;
  background-color: var(--background);
  border-radius: 8px;
`;

// Modal Actions
export const ModalActions = styled.div`
  margin-top: 25px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`;
